import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MessageCircle, Phone, MapPin, TreePine, Target, Landmark } from "lucide-react";

const ASSETS = "https://customer-assets.emergentagent.com/job_monterias-hub/artifacts/";

const IMG = {
  hero: ASSETS + "dbwusx69_e9bd79db-e32f-46da-9a04-3234ef98b6c1.jpg",
  laLapa: ASSETS + "ox31i7sf_088c4944-07ca-448e-bd5a-bdedddb98c44.jpg",
  losPerales: ASSETS + "p9itaag3_imag0113-standard.webp",
  laCascada: ASSETS + "5opjgbai_00323873-3724-4938-af85-200c71dd0cae.jpg",
  laPicota: ASSETS + "ohya2bq7_image-high-uhkbyx.webp",
};

const STATS = [
  { value: "4.500+", label: "Hectáreas", icon: TreePine },
  { value: "4", label: "Fincas privadas", icon: Landmark },
  { value: "20+", label: "Modalidades", icon: Target },
];

const FINCAS = [
  { id: "la-lapa", name: "Finca La Lapa", species: "Ciervo · Jabalí", desc: "Sierra y dehesa, terreno mixto ideal para montería y rececho.", image: IMG.laLapa },
  { id: "los-perales", name: "Finca Los Perales", species: "Jabalí · Ciervas · Venados", desc: "Dehesa El Chaparral — caza menor y aguardo de jabalí.", image: IMG.losPerales },
  { id: "la-cascada", name: "Finca La Cascada", species: "Jabalí · Cierva · Venados", desc: "Veredas de agua y vegetación cerrada — el escenario más salvaje.", image: IMG.laCascada },
  { id: "la-picota", name: "Finca La Picota", species: "Perdiz Roja · Zorzal · Jabalí", desc: "Dehesa abierta y olivar para caza menor y mayor selectiva.", image: "https://customer-assets-4nw71qhi.emergentagent.net/job_club-premium-caza/artifacts/1179fwcz_unnamed.webp" },
];

const MODALIDADES = [
  { n: "01", name: "Aguardo de Jabalí", desc: "Paciencia, estrategia y la magia de la noche." },
  { n: "02", name: "Conejos", desc: "Clásico, dinámico y muy arraigado en la cultura cinegética." },
  { n: "03", name: "Media Veda (Paloma Torcaz)", desc: "Movimientos migratorios, rapidez y precisión." },
  { n: "04", name: "Perdiz con Reclamo", desc: "La modalidad más tradicional de la caza menor andaluza." },
  { n: "05", name: "Zorzales", desc: "Vuelo veloz, destreza y rapidez del cazador." },
  { n: "06", name: "Caza con Arco", desc: "Una práctica milenaria que gana popularidad en Europa." },
  { n: "07", name: "Perdiz o Faisán al Salto", desc: "Caza sembrada de la mayor calidad disponible." },
  { n: "08", name: "Suelta de Perdiz, Codorniz y Faisán", desc: "Sueltas a medida para grupos de 8 a 20 personas." },
];

const WA = "https://wa.me/34678783000?text=" + encodeURIComponent("Hola, me gustaría reservar una jornada de caza con Cinegética Ruta del Agua.");

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: "easeOut" },
};

export default function Fincas() {
  return (
    <div data-testid="fincas-page">
      {/* HERO */}
      <section className="relative min-h-[80vh] flex items-center">
        <div className="absolute inset-0">
          <img src={IMG.hero} alt="Sierra andaluza" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-carbon via-transparent to-carbon/60" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-28 w-full">
          <motion.p {...fadeUp} className="overline-gold mb-5">Guillena · Sevilla · 4.500+ hectáreas</motion.p>
          <motion.h1 {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="hero-text-shadow text-4xl sm:text-5xl lg:text-7xl font-light text-white leading-[1.02] tracking-tight max-w-3xl"
            data-testid="fincas-hero-title">
            Pasión por la caza, <span className="text-gold italic font-medium">tradición por la tierra</span>
          </motion.h1>
          <motion.p {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.2 }}
            className="mt-6 text-white/80 text-base lg:text-lg font-light max-w-xl hero-text-shadow">
            Cuatro fincas, decenas de aguardos y amantes del campo.
            Cinegética Ruta del Agua de Guillena, gestionada por Javier Zambrano y Antonio Zambrano.
          </motion.p>
          <motion.div {...fadeUp} transition={{ ...fadeUp.transition, delay: 0.3 }} className="mt-10 flex flex-col sm:flex-row gap-4">
            <a href={WA} target="_blank" rel="noopener noreferrer" className="btn-gold-solid" data-testid="fincas-reservar-btn">
              <MessageCircle size={16} /> Reservar Jornada
            </a>
            <a href="#fincas" className="btn-ghost-light" data-testid="fincas-descubrir-btn">Descubrir las Fincas</a>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-y border-white/5 bg-[#0D0D0D]" data-testid="fincas-stats">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 grid grid-cols-1 sm:grid-cols-3 gap-10">
          {STATS.map((s, i) => (
            <motion.div key={s.label} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.08 }} className="flex flex-col items-start gap-2">
              <s.icon size={22} strokeWidth={1.25} className="text-gold" />
              <p className="font-heading text-4xl lg:text-5xl text-white font-medium">{s.value}</p>
              <p className="text-xs uppercase tracking-[0.2em] text-white/40">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* SOBRE NOSOTROS */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24" data-testid="sobre-nosotros">
        <motion.div {...fadeUp} className="max-w-3xl">
          <p className="overline-gold mb-4">Sobre nosotros</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white font-light tracking-tight">
            Una vida en el campo, <span className="text-gold italic">una pasión compartida</span>
          </h2>
          <p className="mt-6 text-white/65 font-light leading-relaxed">
            <b className="text-white">Cinegética Ruta del Agua de Guillena</b> está gestionada por <b className="text-white">Javier Zambrano y Antonio Zambrano</b>,
            cazadores apasionados de todas las modalidades de caza. Esta empresa nace con el firme compromiso de ofrecer a los
            aficionados una experiencia única y enriquecedora.
          </p>
          <p className="mt-4 text-white/65 font-light leading-relaxed">
            Disponemos de más de <b className="text-gold">4.500 hectáreas</b> repartidas en cuatro fincas cuidadosamente gestionadas:
            La Lapa, Los Perales, La Cascada y La Picota. Cada una con su propia personalidad y especies emblemáticas.
          </p>
        </motion.div>
      </section>

      {/* FINCAS */}
      <section id="fincas" className="max-w-7xl mx-auto px-5 lg:px-8 pb-24" data-testid="fincas-grid">
        <motion.div {...fadeUp} className="mb-12">
          <p className="overline-gold mb-4">Nuestras fincas</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white font-light tracking-tight">
            Cuatro escenarios, <span className="text-gold italic">una sola pasión</span>
          </h2>
          <p className="mt-4 text-white/55 font-light max-w-2xl">
            Desde los riscos de la sierra hasta los llanos cerealistas. Cada finca es un mundo.
          </p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {FINCAS.map((f, i) => (
            <motion.article key={f.id} {...fadeUp} transition={{ ...fadeUp.transition, delay: (i % 2) * 0.1 }}
              className="card-dark overflow-hidden group" data-testid={`finca-${f.id}`}>
              <div className="relative h-72 overflow-hidden">
                <img src={f.image} alt={f.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
                <span className="absolute top-4 left-4 bg-black/70 backdrop-blur text-gold text-[10px] uppercase tracking-[0.2em] px-3 py-1.5">
                  {f.species}
                </span>
              </div>
              <div className="p-7">
                <h3 className="text-2xl lg:text-3xl text-white font-medium">{f.name}</h3>
                <p className="mt-2 text-white/60 text-sm font-light">{f.desc}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* MODALIDADES */}
      <section className="bg-forest border-y border-white/5" data-testid="modalidades">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <motion.div {...fadeUp} className="mb-12">
            <p className="overline-gold mb-4">Modalidades de caza</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white font-light tracking-tight">
              De la montería <span className="text-gold italic">al ojeo de perdiz</span>
            </h2>
            <p className="mt-4 text-white/55 font-light max-w-2xl">
              Practicamos y organizamos todas las modalidades reconocidas, con rehala propia, guías locales y respeto absoluto a la tradición.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {MODALIDADES.map((m, i) => (
              <motion.div key={m.n} {...fadeUp} transition={{ ...fadeUp.transition, delay: (i % 4) * 0.07 }}
                className="border-l border-gold/30 pl-5" data-testid={`modalidad-${m.n}`}>
                <p className="font-heading text-3xl text-gold/60">{m.n}</p>
                <h3 className="mt-2 text-lg text-white font-medium">{m.name}</h3>
                <p className="mt-2 text-white/55 text-sm font-light leading-relaxed">{m.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA RESERVA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24 text-center" data-testid="fincas-cta">
        <motion.div {...fadeUp}>
          <p className="overline-gold mb-4">¿Listo para el campo?</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl text-white font-light tracking-tight">
            Planifiquemos tu próxima <span className="text-gold italic">jornada juntos</span>
          </h2>
          <p className="mt-5 text-white/60 font-light max-w-xl mx-auto">
            Cuéntanos qué buscas — modalidad, fechas, número de cazadores — y diseñaremos una experiencia a tu medida en alguna de nuestras fincas.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a href={WA} target="_blank" rel="noopener noreferrer" className="btn-gold-solid" data-testid="fincas-cta-whatsapp">
              <MessageCircle size={16} /> Reservar por WhatsApp
            </a>
            <Link to="/acciones-de-caza" className="btn-ghost-light" data-testid="fincas-cta-ofertas">Ver Ofertas de Temporada</Link>
          </div>
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-14">
            <a href="tel:+34678783000" className="flex items-center gap-3 group" data-testid="fincas-phone-antonio">
              <Phone size={18} strokeWidth={1.5} className="text-gold" />
              <span className="text-left">
                <span className="block text-[10px] uppercase tracking-[0.2em] text-white/40">Antonio Zambrano</span>
                <span className="text-lg text-white group-hover:text-gold transition-colors duration-200">+34 678 783 000</span>
              </span>
            </a>
            <a href="tel:+34664069630" className="flex items-center gap-3 group" data-testid="fincas-phone-javier">
              <Phone size={18} strokeWidth={1.5} className="text-gold" />
              <span className="text-left">
                <span className="block text-[10px] uppercase tracking-[0.2em] text-white/40">Javier Zambrano</span>
                <span className="text-lg text-white group-hover:text-gold transition-colors duration-200">+34 664 069 630</span>
              </span>
            </a>
          </div>
          <p className="mt-8 flex items-center justify-center gap-2 text-white/40 text-sm font-light">
            <MapPin size={15} className="text-gold" /> Ctra. Valdeplatilla Km 4, Guillena (Sevilla)
          </p>
        </motion.div>
      </section>
    </div>
  );
}
